import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./DailyControl.module.scss";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Button,
  Typography,
  Tooltip,
  Space,
  Progress,
  Skeleton,
} from "antd";
import {
  ExportOutlined,
  CalendarOutlined,
  BulbOutlined,
  FlagOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DashboardOutlined,
  AimOutlined,
  BarChartOutlined,
  RiseOutlined,
  ThunderboltOutlined,
  FallOutlined,
  TagOutlined,
} from "@ant-design/icons";
import moment from "moment";
import * as XLSX from "xlsx";
import { getDailyControlSummary, getDailyControlGaps } from "../../../api/Services";
import {
  DailyControlSummaryData,
  DailyControlGapsData,
  DailyControlDailyRow,
  DailyControlGapBreakdown,
} from "../../../types/dailyControl";

const { Title, Text } = Typography;

const formatCurrency = (v: number) =>
  `$${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;

const formatPct = (v: number, showSign = false) => {
  const sign = showSign && v > 0 ? "+" : "";
  return `${sign}${v.toFixed(1)}%`;
};

const STATUS_COLOR_ICON: Record<string, { color: string; icon: React.ReactNode }> = {
  ALINEADO: { color: "green", icon: <CheckCircleOutlined /> },
  ATENCION: { color: "orange", icon: <WarningOutlined /> },
  EN_RIESGO: { color: "red", icon: <CloseCircleOutlined /> },
};

const STATUS_LABEL_KEY: Record<string, string> = {
  ALINEADO: "DailyControl-Estado-Alineado",
  ATENCION: "DailyControl-Estado-Atencion",
  EN_RIESGO: "DailyControl-Estado-EnRiesgo",
};

const PROBABILITY_COLOR: Record<string, string> = {
  ALTA: "green",
  MEDIA: "orange",
  BAJA: "red",
};

const PROBABILITY_LABEL_KEY: Record<string, string> = {
  ALTA: "DailyControl-Probabilidad-Alta",
  MEDIA: "DailyControl-Probabilidad-Media",
  BAJA: "DailyControl-Probabilidad-Baja",
};

const DIAGNOSTIC_ICONS: Record<string, React.ReactNode> = {
  GAP_ACUMULADO: <BarChartOutlined />,
  NIVEL_CUMPLIMIENTO: <AimOutlined />,
  SEMANA_CRITICA: <CalendarOutlined />,
  PRINCIPAL_EJE_GAP: <ThunderboltOutlined />,
  FORECAST_CIERRE: <RiseOutlined />,
  SENAL_RECUPERACION: <DashboardOutlined />,
  SENAL_DETERIORO: <FallOutlined />,
  PROMOCIONES_VENTA: <TagOutlined />,
};

const buildGapColumns = (axisLabel: string, t: (key: string) => string) => [
  {
    title: axisLabel,
    dataIndex: "name",
    key: "name",
    sorter: (a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""),
  },
  {
    title: t("DailyControl-Column-PctGapExplicado"),
    dataIndex: "gapExplainedPct",
    key: "gapExplainedPct",
    sorter: (a: any, b: any) => a.gapExplainedPct - b.gapExplainedPct,
    render: (v: number) => (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Progress
          percent={Math.min(Math.abs(v), 100)}
          size="small"
          showInfo={false}
          strokeColor="#f5222d"
          style={{ flex: 1, maxWidth: 120 }}
        />
        <span>{v.toFixed(0)}%</span>
      </div>
    ),
    align: "right" as const,
    width: 200,
  },
  {
    title: t("DailyControl-Column-AporteGap"),
    dataIndex: "gapAmount",
    key: "gapAmount",
    sorter: (a: any, b: any) => a.gapAmount - b.gapAmount,
    render: (v: number) => (
      <span style={{ color: v <= 0 ? "#f5222d" : "#52c41a", fontWeight: 600 }}>
        {formatCurrency(v)}
      </span>
    ),
    align: "right" as const,
  },
];

const GapBreakdownTable: React.FC<{ breakdown?: DailyControlGapBreakdown; pageSize?: number }> = ({
  breakdown,
  pageSize,
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.gapSection}>
      <Text strong className={styles.gapTitle}>
        {breakdown?.axisLabel ?? ""}
      </Text>
      <Table
        columns={buildGapColumns(breakdown?.axisLabel ?? "", t)}
        dataSource={breakdown?.items ?? []}
        rowKey={(r) => String(r.id)}
        pagination={pageSize ? { pageSize, size: "small", showSizeChanger: false } : false}
        size="small"
        className={styles.dataTable}
      />
    </div>
  );
};

const DailyControl: React.FC = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<DailyControlSummaryData | null>(null);
  const [gaps, setGaps] = useState<DailyControlGapsData | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingGaps, setLoadingGaps] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(moment());

  useEffect(() => {
    setLoadingSummary(true);
    setLoadingGaps(true);

    getDailyControlSummary()
      .then((res) => {
        setSummary(res);
        setLastUpdated(moment());
      })
      .finally(() => setLoadingSummary(false));

    getDailyControlGaps()
      .then((res) => setGaps(res))
      .finally(() => setLoadingGaps(false));
  }, []);

  const kpis = summary?.kpis;
  const monthStatus = gaps?.monthStatus;
  const statusColorIcon = STATUS_COLOR_ICON[monthStatus?.status ?? "EN_RIESGO"];
  const statusLabel = t(STATUS_LABEL_KEY[monthStatus?.status ?? "EN_RIESGO"]);
  const complianceProbability = monthStatus?.complianceProbability;

  const handleExport = () => {
    const rows = (summary?.dailyRows ?? []).map((r: DailyControlDailyRow) => ({
      [t("DailyControl-Column-Dia")]: r.day,
      [t("DailyControl-Column-Fecha")]: moment(r.date).format("DD/MM/YYYY"),
      [t("DailyControl-Column-Semana")]: r.week,
      [t("DailyControl-Column-MetaDiaria")]: r.dailyTarget,
      [t("DailyControl-Column-VentaReal")]: r.actualSales ?? "",
      [t("DailyControl-Column-AcumMeta")]: r.accumulatedTarget,
      [t("DailyControl-Column-AcumReal")]: r.accumulatedActual ?? "",
      [t("DailyControl-Column-Desvio")]: r.gap ?? "",
      [t("DailyControl-Column-PctCumplimientoShort")]: r.compliancePct ?? "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("DailyControl-DailyTable-Title"));
    XLSX.writeFile(wb, `control-diario-${moment().format("YYYY-MM")}.xlsx`);
  };

  const weekColumns = [
    { title: t("DailyControl-Column-Semana"), dataIndex: "week", key: "week" },
    {
      title: t("DailyControl-Column-PctDistribucion"),
      dataIndex: "distributionPct",
      key: "distributionPct",
      align: "right" as const,
      render: (v: number) => formatPct(v),
    },
    {
      title: t("DailyControl-Column-MetaSemanal"),
      dataIndex: "weeklyTarget",
      key: "weeklyTarget",
      render: (v: number) => formatCurrency(v),
      align: "right" as const,
    },
  ];

  const dayOfWeekColumns = [
    { title: t("DailyControl-Column-Dia"), dataIndex: "day", key: "day" },
    {
      title: t("DailyControl-Column-PctDistribucion"),
      dataIndex: "distributionPct",
      key: "distributionPct",
      align: "right" as const,
      render: (v: number) => formatPct(v),
    },
    {
      title: t("DailyControl-Column-VentaHistorica"),
      dataIndex: "historicalReference",
      key: "historicalReference",
      render: (v: number) => formatCurrency(v),
      align: "right" as const,
    },
  ];

  const dailyColumns = [
    { title: t("DailyControl-Column-Dia"), dataIndex: "day", key: "day", width: 55 },
    {
      title: t("DailyControl-Column-Fecha"),
      dataIndex: "date",
      key: "date",
      width: 70,
      render: (v: string) => moment(v).format("DD/MM"),
    },
    { title: t("DailyControl-Column-Semana"), dataIndex: "week", key: "week", width: 65 },
    {
      title: t("DailyControl-Column-MetaDiaria"),
      dataIndex: "dailyTarget",
      key: "dailyTarget",
      render: (v: number) => formatCurrency(v),
      align: "right" as const,
      width: 110,
    },
    {
      title: t("DailyControl-Column-VentaReal"),
      dataIndex: "actualSales",
      key: "actualSales",
      render: (v: number | null) => (v !== null ? formatCurrency(v) : "—"),
      align: "right" as const,
      width: 110,
    },
    {
      title: t("DailyControl-Column-AcumMeta"),
      dataIndex: "accumulatedTarget",
      key: "accumulatedTarget",
      render: (v: number) => formatCurrency(v),
      align: "right" as const,
      width: 120,
    },
    {
      title: t("DailyControl-Column-AcumReal"),
      dataIndex: "accumulatedActual",
      key: "accumulatedActual",
      render: (v: number | null) => (v !== null ? formatCurrency(v) : "—"),
      align: "right" as const,
      width: 120,
    },
    {
      title: t("DailyControl-Column-Desvio"),
      dataIndex: "gap",
      key: "gap",
      render: (v: number | null) =>
        v !== null ? (
          <span style={{ color: v >= 0 ? "#52c41a" : "#f5222d", fontWeight: 600 }}>
            {v >= 0 ? "+" : ""}
            {formatCurrency(v)}
          </span>
        ) : (
          "—"
        ),
      align: "right" as const,
      width: 110,
    },
    {
      title: t("DailyControl-Column-PctCumplimientoShort"),
      dataIndex: "compliancePct",
      key: "compliancePct",
      render: (v: number | null) =>
        v !== null ? (
          <span style={{ color: v >= 100 ? "#52c41a" : "#f5222d", fontWeight: 600 }}>
            {formatPct(v)}
          </span>
        ) : (
          "—"
        ),
      align: "right" as const,
      width: 100,
    },
  ];

  return (
    <div className={styles.wrapper}>
      {/* ════════ HEADER ════════ */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Title level={3} className={styles.pageTitle}>
            {t("DailyControl-Title")}
          </Title>
          <Text type="secondary" className={styles.lastUpdated}>
            {t("DailyControl-LastUpdated", { date: lastUpdated.format("DD/MM/YYYY HH:mm"), interpolation: { escapeValue: false } })}
          </Text>
        </div>
        <Button icon={<ExportOutlined />} type="primary" onClick={handleExport} disabled={!summary} className={styles.exportButton}>
          {t("DailyControl-ExportSummary")}
        </Button>
      </div>

      {/* ════════ KPIs (summary) ════════ */}
      {loadingSummary && !summary ? (
        <div className={styles.kpiRow}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Card className={styles.kpiCard} key={idx}>
              <Skeleton.Input active size="small" style={{ width: "80%", height: 14, marginBottom: 8 }} />
              <Skeleton.Input active style={{ width: "55%", height: 24 }} />
            </Card>
          ))}
        </div>
      ) : (
        <div className={styles.kpiRow}>
          {[
            { label: t("DailyControl-Kpi-VentaLy"), value: formatCurrency(kpis?.salesLy ?? 0) },
            { label: t("DailyControl-Kpi-GrowthTarget"), value: formatPct(kpis?.growthTargetPct ?? 0) },
            { label: t("DailyControl-Kpi-MetaMensual"), value: formatCurrency(kpis?.monthlyTarget ?? 0) },
            {
              label: t("DailyControl-Kpi-AcumuladoReal"),
              value: formatCurrency(kpis?.accumulatedActual ?? 0),
              highlight: true,
            },
            {
              label: t("DailyControl-Kpi-PctCumplimiento"),
              value: formatPct(kpis?.compliancePct ?? 0),
              highlight: true,
              isGood: (kpis?.compliancePct ?? 0) >= 100,
            },
            { label: t("DailyControl-Kpi-ForecastCierre"), value: formatCurrency(kpis?.closingForecast ?? 0) },
          ].map((kpi, idx) => (
            <Card className={styles.kpiCard} key={idx}>
              <Text className={styles.kpiLabel}>{kpi.label}</Text>
              <div
                className={styles.kpiValue}
                style={
                  kpi.highlight
                    ? { color: kpi.isGood === false ? "#f5222d" : kpi.isGood === true ? "#52c41a" : undefined }
                    : undefined
                }
              >
                {kpi.value}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ════════ DIAGNÓSTICO + ESTADO ════════ */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <BulbOutlined style={{ color: "#faad14" }} />
                <span>{t("DailyControl-Diagnostico-Title")}</span>
              </Space>
            }
          >
            {loadingGaps && !gaps ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              <div className={styles.diagnosticList}>
                {(gaps?.diagnostics ?? []).map((d, idx) => (
                  <div key={idx} className={styles.diagnosticItem}>
                    <span className={styles.diagnosticIcon}>
                      {DIAGNOSTIC_ICONS[d.type] ?? <FlagOutlined />}
                    </span>
                    <Text>{d.text}</Text>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            className={styles.sectionCard}
            title={
              <Space>
                <DashboardOutlined />
                <span>{t("DailyControl-EstadoMes-Title")}</span>
              </Space>
            }
          >
            <div className={styles.estadoBox}>
              {gaps && (
                <Tag icon={statusColorIcon.icon} color={statusColorIcon.color} className={styles.estadoTag}>
                  {statusLabel}
                </Tag>
              )}
              <div className={styles.estadoProgress}>
                <Progress
                  type="dashboard"
                  percent={Math.min(Math.round(kpis?.compliancePct ?? 0), 100)}
                  strokeColor={
                    (kpis?.compliancePct ?? 0) >= 99
                      ? "#52c41a"
                      : (kpis?.compliancePct ?? 0) >= 96
                      ? "#faad14"
                      : "#f5222d"
                  }
                  format={() => formatPct(kpis?.compliancePct ?? 0)}
                  width={130}
                />
              </div>
              <div className={styles.estadoDetails}>
                <div className={styles.estadoRow}>
                  <Text type="secondary">{t("DailyControl-Kpi-ForecastCierre")}</Text>
                  <Text strong>{formatCurrency(kpis?.closingForecast ?? 0)}</Text>
                </div>
                <div className={styles.estadoRow}>
                  <Text type="secondary">{t("DailyControl-EstadoMes-GapEstimado")}</Text>
                  {gaps ? (
                    <Text
                      strong
                      style={{ color: (monthStatus?.estimatedClosingGap ?? 0) >= 0 ? "#52c41a" : "#f5222d" }}
                    >
                      {formatCurrency(monthStatus?.estimatedClosingGap ?? 0)}
                    </Text>
                  ) : (
                    <Skeleton.Input active size="small" style={{ width: 80, height: 16 }} />
                  )}
                </div>
                <div className={styles.estadoRow}>
                  <Text type="secondary">{t("DailyControl-EstadoMes-Probabilidad")}</Text>
                  {gaps ? (
                    <Tooltip title={t("DailyControl-EstadoMes-ProbabilidadTooltip")}>
                      <Tag color={PROBABILITY_COLOR[complianceProbability ?? "BAJA"]}>
                        {t(PROBABILITY_LABEL_KEY[complianceProbability ?? "BAJA"])}
                      </Tag>
                    </Tooltip>
                  ) : (
                    <Skeleton.Input active size="small" style={{ width: 64, height: 22 }} />
                  )}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ════════ DISTRIBUCIONES + TABLA DIARIA (summary) ════════ */}
      <Row gutter={[16, 16]} className={styles.distRow} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12} className={styles.distCol}>
          <Card
            className={`${styles.sectionCard} ${styles.equalHeightCard}`}
            title={
              <Space>
                <CalendarOutlined />
                <span>{t("DailyControl-WeekDist-Title")}</span>
              </Space>
            }
          >
            {loadingSummary && !summary ? (
              <Skeleton active paragraph={{ rows: 5 }} />
            ) : (
              <Table
                columns={weekColumns}
                dataSource={summary?.weeklyDistribution ?? []}
                rowKey="week"
                pagination={false}
                size="small"
                className={styles.dataTable}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12} className={styles.distCol}>
          <Card
            className={`${styles.sectionCard} ${styles.equalHeightCard}`}
            title={
              <Space>
                <CalendarOutlined />
                <span>{t("DailyControl-DowDist-Title")}</span>
              </Space>
            }
          >
            {loadingSummary && !summary ? (
              <Skeleton active paragraph={{ rows: 7 }} />
            ) : (
              <Table
                columns={dayOfWeekColumns}
                dataSource={summary?.weekdayDistribution ?? []}
                rowKey="day"
                pagination={false}
                size="small"
                className={styles.dataTable}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Card
        className={styles.sectionCard}
        title={
          <Space>
            <BarChartOutlined />
            <span>{t("DailyControl-DailyTable-Title")}</span>
          </Space>
        }
      >
        {loadingSummary && !summary ? (
          <Skeleton active paragraph={{ rows: 12 }} />
        ) : (
          <Table
            columns={dailyColumns}
            dataSource={summary?.dailyRows ?? []}
            rowKey="date"
            pagination={false}
            size="small"
            className={styles.dataTable}
            scroll={{ x: 860 }}
            rowClassName={(record: DailyControlDailyRow) =>
              record.isPast ? "" : styles.futureRow
            }
          />
        )}
      </Card>

      {/* ════════ GAP BREAKDOWN (gaps) ════════ */}
      <Card
        className={styles.sectionCard}
        title={
          <Space>
            <ThunderboltOutlined style={{ color: "#f5222d" }} />
            <span>{t("DailyControl-GapExplanation-Title")}</span>
          </Space>
        }
      >
        {loadingGaps ? (
          <>
            <Skeleton.Input active style={{ width: 220, height: 16, marginBottom: 16 }} />
            <Row gutter={[16, 16]}>
              {Array.from({ length: 3 }).map((_, idx) => (
                <Col xs={24} lg={8} key={idx}>
                  <Skeleton.Input active size="small" style={{ width: 80, height: 16, marginBottom: 12 }} />
                  <Skeleton active paragraph={{ rows: 6 }} />
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <>
            <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
              {t("DailyControl-GapExplanation-Total")}:{" "}
              <strong style={{ color: (summary?.totalGap ?? 0) < 0 ? "#f5222d" : "#52c41a" }}>
                {formatCurrency(summary?.totalGap ?? 0)}
              </strong>
            </Text>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={8}>
                <GapBreakdownTable breakdown={gaps?.gapByChannel} />
              </Col>
              <Col xs={24} lg={8}>
                <GapBreakdownTable breakdown={gaps?.gapByStore} pageSize={20} />
              </Col>
              <Col xs={24} lg={8}>
                <GapBreakdownTable breakdown={gaps?.gapByCategory} pageSize={20} />
              </Col>
            </Row>
          </>
        )}
      </Card>
    </div>
  );
};

export default DailyControl;
